import type { QuoteResponseDto } from '@trellis/api'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  useArchiveQuoteMutation,
  useDeleteQuoteMutation,
  useTransitionQuoteStatusMutation,
  useUnarchiveQuoteMutation,
} from '@/hooks/useQuotes'

interface UseQuoteRowActionsOptions {
  /**
   * Called after archive/unarchive/delete/mark-sent succeeds — e.g. to invalidate a
   * client's stats query on the client detail page.
   */
  onMutated?: () => void
}

/**
 * Shared single-row archive/unarchive/delete/mark-sent state and handlers for quotes —
 * used by both QuotesPage and ClientDetailPage so the confirmation copy and mutation
 * wiring stay in one place.
 */
export function useQuoteRowActions({ onMutated }: UseQuoteRowActionsOptions = {}) {
  const [quoteToDelete, setQuoteToDelete] = useState<QuoteResponseDto | null>(null)
  const [markSentQuote, setMarkSentQuote] = useState<QuoteResponseDto | null>(null)

  const deleteMutation = useDeleteQuoteMutation()
  const archiveMutation = useArchiveQuoteMutation()
  const unarchiveMutation = useUnarchiveQuoteMutation()
  const transitionMutation = useTransitionQuoteStatusMutation()

  function handleArchive(quote: QuoteResponseDto) {
    archiveMutation.mutate(quote.id, {
      onSuccess: () => {
        toast.success('Quote archived')
        onMutated?.()
      },
      onError: (caughtError) => {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Failed to archive quote'
        toast.error('Cannot archive quote', { description: message })
      },
    })
  }

  function handleUnarchive(quote: QuoteResponseDto) {
    unarchiveMutation.mutate(quote.id, {
      onSuccess: () => {
        toast.success('Quote unarchived')
        onMutated?.()
      },
      onError: (caughtError) => {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Failed to unarchive quote'
        toast.error('Cannot unarchive quote', { description: message })
      },
    })
  }

  function handleConfirmDelete() {
    if (!quoteToDelete) return
    const quoteId = quoteToDelete.id
    setQuoteToDelete(null)
    deleteMutation.mutate(quoteId, {
      onSuccess: () => {
        toast.success('Quote deleted')
        onMutated?.()
      },
      onError: (caughtError) => {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Failed to delete quote'
        toast.error('Cannot delete quote', { description: message })
      },
    })
  }

  function handleConfirmMarkSent() {
    if (!markSentQuote) return
    const quoteId = markSentQuote.id
    setMarkSentQuote(null)
    transitionMutation.mutate(
      { id: quoteId, data: { status: 'SENT' } },
      {
        onSuccess: () => {
          toast.success('Quote marked as sent')
          onMutated?.()
        },
        onError: (caughtError) => {
          const message =
            caughtError instanceof Error ? caughtError.message : 'Failed to update status'
          toast.error('Failed to update status', { description: message })
        },
      },
    )
  }

  return {
    quoteToDelete,
    setQuoteToDelete,
    markSentQuote,
    setMarkSentQuote,
    handleArchive,
    handleUnarchive,
    handleConfirmDelete,
    handleConfirmMarkSent,
    isDeletePending: deleteMutation.isPending,
    isMarkSentPending: transitionMutation.isPending,
  }
}
